import { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import GridLayout, { Layout } from 'react-grid-layout';
import { FigureComposition } from '@/store/figure-composition';
import { useFigureCompositionStore } from '@/store/figure-composition';
import { Panel } from './Panel';
import { GridOverlay } from './GridOverlay';
import { Rulers } from './Rulers';
import { useFigureEditorUiState } from '@/hooks/useFigureEditorUiState';
import { mmToPx } from '@/lib/applyJournalTemplate';
import { calculatePanelSizeMm, PREVIEW_DPI } from '@/lib/panelScaling';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

interface FigureCanvasProps {
  composition: FigureComposition;
  showRulers?: boolean;
  previewAtJournalSize?: boolean;
  canvasZoom?: number;
  isReadOnly?: boolean; // Set to true for shared/read-only views
}

export function FigureCanvas({ composition, showRulers = false, previewAtJournalSize = false, canvasZoom = 1, isReadOnly = false }: FigureCanvasProps) {
  // Use granular selectors for panels to prevent re-renders when only figureLegend changes
  // This ensures layout only recalculates when panel data actually changes
  const panels = useFigureCompositionStore((state) => state.currentComposition?.panels);
  const grid = useFigureCompositionStore((state) => state.currentComposition?.grid);
  const canvasSize = useFigureCompositionStore((state) => state.currentComposition?.canvasSize);
  const theme = useFigureCompositionStore((state) => state.currentComposition?.theme);
  
  // Use panels from store if available, otherwise fall back to prop (for backwards compatibility)
  const effectivePanels = panels || composition.panels;
  const effectiveGrid = grid || composition.grid;
  const effectiveCanvasSize = canvasSize || composition.canvasSize;
  const effectiveTheme = theme || composition.theme;
  const updatePanelLayout = useFigureCompositionStore((state) => state.updatePanelLayout);
  const updatePanel = useFigureCompositionStore((state) => state.updatePanel);
  const layoutRef = useRef<Layout[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [draggingPanelId, setDraggingPanelId] = useState<string | null>(null);
  const [resizingPanelId, setResizingPanelId] = useState<string | null>(null);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const { gridVisible } = useFigureEditorUiState();
  
  // Drag-select box state
  const [isDragSelecting, setIsDragSelecting] = useState(false);
  const [dragSelectStart, setDragSelectStart] = useState<{ x: number; y: number } | null>(null);
  const [dragSelectEnd, setDragSelectEnd] = useState<{ x: number; y: number } | null>(null);
  const togglePanelSelection = useFigureCompositionStore((state) => state.togglePanelSelection);
  const clearSelection = useFigureCompositionStore((state) => state.clearSelection);

  // Journal limit dimensions (from journal template or default)
  const journalLimitWidthPx = mmToPx(effectiveCanvasSize.width);
  const journalLimitHeightPx = mmToPx(effectiveCanvasSize.height);
  const padding = 32; // p-4 = 16px on each side
  
  // Calculate visual canvas size (grows with panels, minimum = journal limit)
  // This adapts to what the user is actually using
  // Strategy: Use a consistent approach where bin size adapts to visual canvas
  // We'll calculate panel positions using the column width that GridLayout will use
  const visualCanvasSize = useMemo(() => {
    // Start with journal limit as minimum
    let visualWidthPx = journalLimitWidthPx;
    let visualHeightPx = journalLimitHeightPx;
    
    const rowHeightPx = effectiveGrid.rowHeight;
    const marginX = effectiveGrid.margin[0];
    const marginY = effectiveGrid.margin[1];
    
    if (effectivePanels.length === 0) {
      // No panels, use journal limit
      return {
        widthPx: journalLimitWidthPx,
        heightPx: journalLimitHeightPx,
        journalLimitWidthPx,
        journalLimitHeightPx,
      };
    }
    
    // Calculate max panel extent first using journal limit column width
    // This gives us an initial estimate
    const baseColWidthPx = journalLimitWidthPx / effectiveGrid.cols;
    let maxPanelRightPx = journalLimitWidthPx;
    
    effectivePanels.forEach(panel => {
      const panelX = panel.x * (baseColWidthPx + marginX);
      const panelWidth = panel.w * baseColWidthPx + (panel.w - 1) * marginX;
      const panelRightPx = panelX + panelWidth;
      maxPanelRightPx = Math.max(maxPanelRightPx, panelRightPx);
      
      const panelY = panel.y * (rowHeightPx + marginY);
      const panelHeight = panel.h * rowHeightPx + (panel.h - 1) * marginY;
      const panelBottomPx = panelY + panelHeight;
      visualHeightPx = Math.max(visualHeightPx, panelBottomPx);
    });
    
    // Calculate visual canvas width based on panel positions
    // Use base column width for initial calculation
    visualWidthPx = maxPanelRightPx;
    
    // Add padding
    visualWidthPx = Math.max(visualWidthPx, journalLimitWidthPx) + 40;
    visualHeightPx = Math.max(visualHeightPx, journalLimitHeightPx) + 40;
    
    return {
      widthPx: visualWidthPx,
      heightPx: visualHeightPx,
      journalLimitWidthPx,
      journalLimitHeightPx,
    };
  }, [effectivePanels, journalLimitWidthPx, journalLimitHeightPx, effectiveGrid.cols, effectiveGrid.rowHeight, effectiveGrid.margin]);
  
  // Use visual canvas size for grid layout calculations (adapts to user's canvas)
  // This ensures bin/grid size matches what the user is actually using
  const rawAvailableWidthPx = visualCanvasSize.widthPx;
  
  // Calculate dynamic columns to allow panels beyond journal limits
  // Strategy: Keep column count constant, so bin size adapts directly to visual canvas
  // This ensures bin size = visualCanvasWidth / cols, growing with user's canvas
  const baseColWidthPx = journalLimitWidthPx / effectiveGrid.cols;
  
  // Calculate minimum columns needed to cover visual canvas
  // This ensures panels can be positioned beyond journal limits
  const minColsNeeded = Math.ceil(rawAvailableWidthPx / baseColWidthPx);
  
  // IMPORTANT: Use minimum columns needed, not proportional scaling
  // This allows bin size to adapt to visual canvas:
  // - When canvas = journal limit: cols = original cols → colWidth = baseColWidthPx
  // - When canvas grows: cols expand minimally → colWidth adapts to visual canvas
  const dynamicCols = Math.max(
    effectiveGrid.cols, // Minimum = original grid cols
    minColsNeeded // Expand to cover visual canvas (allows bin size to adapt)
  );
  
  // CRITICAL: Eliminate fractional grid math to ensure integer translate3d positions
  // Snap column width to an integer first, then snap total width to match
  // This ensures react-grid-layout never emits fractional translate3d transforms
  const rawColWidth = rawAvailableWidthPx / dynamicCols;
  const snappedColWidth = Math.round(rawColWidth);
  const snappedWidthPx = snappedColWidth * dynamicCols;
  
  // Use snappedWidthPx everywhere to ensure integer column widths
  const availableWidthPx = snappedWidthPx;
  
  // Measure actual DOM container size using ResizeObserver
  // This is used for grid overlay rendering
  useEffect(() => {
    const measureContainer = () => {
      if (containerRef.current) {
        const measuredWidth = containerRef.current.clientWidth;
        const measuredHeight = containerRef.current.clientHeight;
        const expectedWidth = visualCanvasSize.widthPx + padding;
        const expectedHeight = visualCanvasSize.heightPx + padding;
        
        setContainerSize({
          width: measuredWidth > 0 ? measuredWidth : expectedWidth,
          height: measuredHeight > 0 ? measuredHeight : expectedHeight,
        });
      }
    };

    measureContainer();
    const resizeObserver = new ResizeObserver(measureContainer);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [visualCanvasSize.widthPx, visualCanvasSize.heightPx, padding]);

  // Calculate canvas size constraints from journal template
  // This ensures panels respect the journal template's canvas size limits when resizing
  // Note: Grid/bin size uses visual canvas size, but panel constraints use journal limits
  const canvasConstraints = useMemo(() => {
    const journalWidthMm = effectiveCanvasSize.width;
    const journalHeightMm = effectiveCanvasSize.height;
    
    // Use journal limit for constraints (panels can't resize beyond journal limits)
    const journalWidthPx = visualCanvasSize.journalLimitWidthPx;
    const journalHeightPx = visualCanvasSize.journalLimitHeightPx;
    
    // Calculate max columns - panels can't exceed journal limit width
    const maxCols = effectiveGrid.cols;
    
    // Calculate max rows based on journal limit height
    // Each row takes rowHeight + margin[1] pixels (margin between rows)
    const rowHeightWithMargin = effectiveGrid.rowHeight + effectiveGrid.margin[1];
    const maxRows = effectiveGrid.rowHeight > 0 
      ? Math.floor(journalHeightPx / rowHeightWithMargin) 
      : Infinity;
    
    const constraints = {
      maxWidthPx: journalWidthPx,
      maxHeightPx: journalHeightPx,
      maxCols,
      maxRows,
      journalWidthMm,
      journalHeightMm,
    };
    
    return constraints;
  }, [effectiveCanvasSize, effectiveGrid.cols, effectiveGrid.rowHeight, effectiveGrid.margin, visualCanvasSize]);

  const layout = useMemo<Layout[]>(() => {
    const constrainedPanels = effectivePanels.map((panel) => {
      // Constrain max width/height to stay within canvas bounds
      let maxW = panel.maxW;
      let maxH = panel.maxH;
      
      // Always apply canvas constraints to panel max dimensions
      let wasConstrainedW = false;
      let wasConstrainedH = false;
      
      if (canvasConstraints.maxCols < Infinity) {
        // Allow panels to be wider than journal limit to enable aligning single plot with two separate plots
        // Set max width to 2x grid columns to allow matching two plots side by side
        // But allow panels to be positioned beyond journal limits (no constraint on x position)
        // Calculate max width based on grid columns, allowing up to 2x for flexibility
        const maxWidthInCols = effectiveGrid.cols * 2; // Allow up to 2x grid width for flexibility
        const oldMaxW = maxW;
        // Only constrain if panel would exceed 2x grid width when resizing
        // But don't prevent panels from being positioned beyond journal limits
        if (maxW === undefined) {
          // If no explicit maxW, allow up to 2x grid width
          maxW = maxWidthInCols;
          wasConstrainedW = true;
        } else if (maxW > maxWidthInCols) {
          // Constrain to 2x grid width maximum
          maxW = maxWidthInCols;
          wasConstrainedW = true;
        }
        if (wasConstrainedW && oldMaxW !== undefined) {
          console.log(`🔒 [FigureCanvas] Panel ${panel.id} width constrained: ${oldMaxW} → ${maxW} cols (max allowed: ${maxWidthInCols} cols = 2x grid)`);
        }
      }
      
      if (canvasConstraints.maxRows < Infinity) {
        // Limit panel height to not exceed journal limit height (only when resizing)
        // But allow panels to be positioned beyond journal limits (no constraint on y position)
        // Calculate max height based on journal limit, not current position
        const maxHeightInRows = canvasConstraints.maxRows; // Allow full height regardless of position
        const oldMaxH = maxH;
        // Only constrain if panel would exceed journal limit when resizing
        // But don't prevent panels from being positioned beyond journal limits
        if (maxH === undefined) {
          maxH = maxHeightInRows;
          wasConstrainedH = true;
        } else if (maxH > maxHeightInRows) {
          maxH = maxHeightInRows;
          wasConstrainedH = true;
        }
        if (wasConstrainedH && oldMaxH !== undefined) {
          console.log(`🔒 [FigureCanvas] Panel ${panel.id} height constrained: ${oldMaxH} → ${maxH} rows (journal limit: ${canvasConstraints.maxRows} rows)`);
        }
      }
      
      return {
        i: panel.id,
        x: panel.x,
        y: panel.y,
        w: panel.w,
        h: panel.h,
        minW: panel.minW,
        minH: panel.minH,
        maxW: maxW,
        maxH: maxH,
        // Disable dragging/resizing if panel is locked OR if it has no plot assigned (to allow clicks)
        isDraggable: !panel.locked && !!panel.plotId,
        isResizable: !panel.locked && !!panel.plotId,
      };
    });
    
    // Log panel constraints summary
    if (effectivePanels.length > 0) {
      const panelsWithConstraints = constrainedPanels.map((p, idx) => {
        const originalPanel = effectivePanels[idx];
        const wasConstrainedW = originalPanel.maxW !== undefined && p.maxW !== undefined && p.maxW !== originalPanel.maxW;
        const wasConstrainedH = originalPanel.maxH !== undefined && p.maxH !== undefined && p.maxH !== originalPanel.maxH;
        const wasConstrained = wasConstrainedW || wasConstrainedH;
        
        return {
          id: p.i,
          position: `(${p.x}, ${p.y})`,
          size: `${p.w}×${p.h}`,
          maxSize: `${p.maxW ?? 'unset'}×${p.maxH ?? 'unset'}`,
          originalMaxSize: originalPanel.maxW !== undefined || originalPanel.maxH !== undefined
            ? `${originalPanel.maxW ?? 'unset'}×${originalPanel.maxH ?? 'unset'}`
            : 'unset',
          constrained: wasConstrained ? '⚠️ YES' : '✓ No',
          constraintDetails: wasConstrained 
            ? `${wasConstrainedW ? `W:${originalPanel.maxW}→${p.maxW}` : ''}${wasConstrainedW && wasConstrainedH ? ', ' : ''}${wasConstrainedH ? `H:${originalPanel.maxH}→${p.maxH}` : ''}`
            : 'none',
        };
      });
      
      const constrainedCount = panelsWithConstraints.filter(p => p.constrained === '⚠️ YES').length;
      console.log(`📊 [FigureCanvas] Layout calculated for ${effectivePanels.length} panel(s):`, {
        journalTemplate: effectiveTheme.journalTemplate || 'none',
        canvasConstraints: {
          maxCols: `${canvasConstraints.maxCols} cols`,
          maxRows: canvasConstraints.maxRows === Infinity ? 'unlimited' : `${canvasConstraints.maxRows} rows`,
          journalLimit: `${canvasConstraints.journalWidthMm}mm × ${canvasConstraints.journalHeightMm}mm`,
        },
        panels: panelsWithConstraints,
        summary: {
          totalPanels: effectivePanels.length,
          constrainedPanels: constrainedCount,
        },
      });
      
      // Log panel details directly
      panelsWithConstraints.forEach((panel) => {
        if (panel.constrained === '⚠️ YES') {
          console.log(`   ⚠️ Panel ${panel.id}: Constrained from ${panel.originalMaxSize} to ${panel.maxSize} ${panel.constraintDetails ? `(${panel.constraintDetails})` : ''}`);
        } else {
          const maxCols = canvasConstraints.maxCols;
          const maxRows = canvasConstraints.maxRows === Infinity ? 'unlimited' : canvasConstraints.maxRows;
          const panelMaxW = panel.maxSize.split('×')[0];
          const panelMaxH = panel.maxSize.split('×')[1];
          const atCanvasLimit = (panelMaxW === String(maxCols) || panelMaxW === 'unset') && 
                                (panelMaxH === String(maxRows) || panelMaxH === 'unset');
          console.log(`   ✓ Panel ${panel.id}: ${panel.size} (max: ${panel.maxSize}) - ${atCanvasLimit ? '✅ At canvas limit' : '✅ Within canvas bounds'}`);
        }
      });
      
      if (constrainedCount === 0 && effectivePanels.length > 0) {
        console.log(`   ✅ All panels are within canvas bounds! Canvas constraints are active:`);
        console.log(`      - Max width: ${canvasConstraints.maxCols} cols`);
        console.log(`      - Max height: ${canvasConstraints.maxRows === Infinity ? 'unlimited' : `${canvasConstraints.maxRows} rows`}`);
        console.log(`      - Panels cannot exceed these limits when resizing`);
      }
    }
    
    return constrainedPanels;
  }, [effectivePanels, canvasConstraints, effectiveTheme.journalTemplate]);

  const handleDragStart = (layout: Layout[], oldItem: Layout, newItem: Layout, placeholder: Layout, e: MouseEvent, element: HTMLElement) => {
    setDraggingPanelId(newItem.i);
  };

  const swapTargetRef = useRef<string | null>(null);

  const handleDrag = (layout: Layout[], oldItem: Layout, newItem: Layout, placeholder: Layout, e: MouseEvent, element: HTMLElement) => {
    if (draggingPanelId === newItem.i) {
      // Check for panel swap: if dragging over another panel's position
      // Only swap if the dragged panel's center overlaps with another panel's position
      const draggedPanel = effectivePanels.find(p => p.id === newItem.i);
      if (draggedPanel) {
        // Calculate center of dragged panel
        const draggedCenterX = newItem.x + newItem.w / 2;
        const draggedCenterY = newItem.y + newItem.h / 2;
        
        // Find panels whose position contains the dragged panel's center
        const panelsAtNewPos = effectivePanels.filter(p => {
          if (p.id === newItem.i || p.id === swapTargetRef.current) return false;
          
          // Check if dragged center is within target panel's bounds
          const targetRight = p.x + p.w;
          const targetBottom = p.y + p.h;
          
          return (
            draggedCenterX >= p.x &&
            draggedCenterX < targetRight &&
            draggedCenterY >= p.y &&
            draggedCenterY < targetBottom
          );
        });
        
        if (panelsAtNewPos.length > 0 && swapTargetRef.current !== panelsAtNewPos[0].id) {
          // Swap positions with the first overlapping panel
          const targetPanel = panelsAtNewPos[0];
          swapTargetRef.current = targetPanel.id;
          
          // Store original positions
          const draggedX = draggedPanel.x;
          const draggedY = draggedPanel.y;
          const draggedW = draggedPanel.w;
          const draggedH = draggedPanel.h;
          const targetX = targetPanel.x;
          const targetY = targetPanel.y;
          const targetW = targetPanel.w;
          const targetH = targetPanel.h;
          
          // Swap positions and sizes
          updatePanel(draggedPanel.id, { x: targetX, y: targetY, w: targetW, h: targetH });
          updatePanel(targetPanel.id, { x: draggedX, y: draggedY, w: draggedW, h: draggedH });
        }
      }
    }
  };

  const handleDragStop = () => {
    setDraggingPanelId(null);
    swapTargetRef.current = null; // Reset swap target
  };

  const handleLayoutChange = (currentLayout: Layout[]) => {
    // GridLayout (non-responsive) provides layout directly, not breakpoint-based layouts
    const prev = layoutRef.current;

    currentLayout.forEach((item) => {
      const prevItem = prev.find((p) => p.i === item.i);
      if (
        !prevItem ||
        prevItem.x !== item.x ||
        prevItem.y !== item.y ||
        prevItem.w !== item.w ||
        prevItem.h !== item.h
      ) {
        updatePanelLayout(item.i, {
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h,
        });
      }
    });

    layoutRef.current = currentLayout;
  };


  const handleResizeStart = (layout: Layout[], oldItem: Layout, newItem: Layout, placeholder: Layout, e: MouseEvent, element: HTMLElement) => {
    setResizingPanelId(newItem.i);
  };

  const handleResize = (layout: Layout[], oldItem: Layout, newItem: Layout, placeholder: Layout, e: MouseEvent, element: HTMLElement) => {
    // Resize handler - no additional logic needed during resize (preview mode handles it)
  };

  const handleResizeStop = (layout: Layout[], oldItem: Layout, newItem: Layout, placeholder: Layout, e: MouseEvent, element: HTMLElement) => {
    // Calculate panel size in mm and update panel
    const resizedPanel = effectivePanels.find(p => p.id === newItem.i);
    if (resizedPanel) {
      const { widthMm, heightMm } = calculatePanelSizeMm(
        newItem.w,
        newItem.h,
        effectiveCanvasSize.width,
        effectiveCanvasSize.height,
        effectiveGrid.cols,
        effectiveGrid.rowHeight,
        effectiveGrid.margin,
        PREVIEW_DPI
      );
      
      // Update panel with mm dimensions
      updatePanel(newItem.i, {
        panelWidthMm: widthMm,
        panelHeightMm: heightMm,
      });
    }
    
    setResizingPanelId(null);
  };

  const setSelectedPanelId = useFigureCompositionStore((state) => state.setSelectedPanelId);

  // Handle drag-select box
  const handleMouseDown = (e: React.MouseEvent) => {
    // Only start drag-select if clicking on canvas background (not on a panel or grid item)
    const target = e.target as HTMLElement;
    
    // Don't start drag-select if clicking on:
    // - A panel or any element inside a panel
    // - A grid layout item (react-grid-layout handles its own drag)
    // - A resize handle
    // - Any interactive element
    if (
      target.closest('[data-panel-id]') ||
      target.closest('.react-grid-item') ||
      target.closest('.react-resizable-handle') ||
      target.closest('button') ||
      target.closest('input') ||
      target.closest('select') ||
      target.closest('svg') // Don't interfere with Plotly plots
    ) {
      return;
    }
    
    // Check if clicking on the grid layout container itself (empty space between panels)
    const gridLayout = containerRef.current?.querySelector('.react-grid-layout');
    const isOnGridLayoutBackground = gridLayout && (
      target === gridLayout ||
      (target.parentElement === gridLayout && !target.closest('.react-grid-item'))
    );
    
    // Or clicking directly on canvas background
    const isOnCanvasBackground = target === e.currentTarget;
    
    if (isOnCanvasBackground || isOnGridLayoutBackground) {
      // Don't prevent default - let react-grid-layout handle its own events
      // But start our drag-select
      setIsDragSelecting(true);
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        setDragSelectStart({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
        setDragSelectEnd({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      }
      // Clear selection if not holding Ctrl/Cmd
      if (!e.ctrlKey && !e.metaKey) {
        clearSelection();
      }
    }
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isDragSelecting && dragSelectStart && containerRef.current) {
      e.preventDefault(); // Prevent text selection during drag-select
      const rect = containerRef.current.getBoundingClientRect();
      setDragSelectEnd({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  }, [isDragSelecting, dragSelectStart]);

  const handleMouseUp = useCallback((e: MouseEvent) => {
    if (isDragSelecting && dragSelectStart && dragSelectEnd && containerRef.current) {
      // Find panels that intersect with the selection box
      const panels = effectivePanels;
      const selectedPanels: string[] = [];
      
      const minX = Math.min(dragSelectStart.x, dragSelectEnd.x);
      const maxX = Math.max(dragSelectStart.x, dragSelectEnd.x);
      const minY = Math.min(dragSelectStart.y, dragSelectEnd.y);
      const maxY = Math.max(dragSelectStart.y, dragSelectEnd.y);
      
      // Get grid layout element to calculate panel positions
      const gridElement = containerRef.current.querySelector('.react-grid-layout');
      if (gridElement) {
        panels.forEach((panel) => {
          const panelElement = gridElement.querySelector(`[data-panel-id="${panel.id}"]`) as HTMLElement;
          if (panelElement) {
            const panelRect = panelElement.getBoundingClientRect();
            const containerRect = containerRef.current!.getBoundingClientRect();
            const panelX = panelRect.left - containerRect.left;
            const panelY = panelRect.top - containerRect.top;
            const panelW = panelRect.width;
            const panelH = panelRect.height;
            
            // Check if panel intersects with selection box
            if (
              panelX < maxX &&
              panelX + panelW > minX &&
              panelY < maxY &&
              panelY + panelH > minY
            ) {
              selectedPanels.push(panel.id);
            }
          }
        });
      }
      
      // Update selection
      if (selectedPanels.length > 0) {
        if (e.ctrlKey || e.metaKey) {
          // Add to existing selection
          selectedPanels.forEach((id) => togglePanelSelection(id));
        } else {
          // Replace selection
          setSelectedPanelId(selectedPanels[0]);
          selectedPanels.slice(1).forEach((id) => togglePanelSelection(id));
        }
      }
      
      setIsDragSelecting(false);
      setDragSelectStart(null);
      setDragSelectEnd(null);
    }
  }, [isDragSelecting, dragSelectStart, dragSelectEnd, effectivePanels, togglePanelSelection, setSelectedPanelId]);

  useEffect(() => {
    if (isDragSelecting) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragSelecting, handleMouseMove, handleMouseUp]);

  const handleCanvasClick = (e: React.MouseEvent) => {
    // Click on empty canvas area deselects (unless we're drag-selecting)
    if (e.target === e.currentTarget && !isDragSelecting) {
      setSelectedPanelId(null);
    }
  };

  // Calculate selection box rectangle
  const selectionBoxStyle: React.CSSProperties | null =
    isDragSelecting && dragSelectStart && dragSelectEnd
      ? {
          position: 'absolute',
          left: `${Math.min(dragSelectStart.x, dragSelectEnd.x)}px`,
          top: `${Math.min(dragSelectStart.y, dragSelectEnd.y)}px`,
          width: `${Math.abs(dragSelectEnd.x - dragSelectStart.x)}px`,
          height: `${Math.abs(dragSelectEnd.y - dragSelectStart.y)}px`,
          border: '2px dashed #3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          pointerEvents: 'none',
          zIndex: 1000,
        }
      : null;

  return (
    <div 
      ref={containerRef}
      className="bg-gray-100 p-4 rounded-lg relative mx-auto overflow-auto"
      style={{ 
        // Use snapped width + padding for container dimensions to ensure integer grid positions
        // Canvas grows beyond journal limits, but minimum = journal limit
        width: `${availableWidthPx + padding}px`,
        height: `${visualCanvasSize.heightPx + padding}px`,
        minWidth: `${visualCanvasSize.journalLimitWidthPx + padding}px`,
        minHeight: `${visualCanvasSize.journalLimitHeightPx + padding}px`,
        // Prevent flex shrinking to maintain canvas-based width
        flexShrink: 0,
      }}
      onClick={handleCanvasClick}
      onMouseDown={handleMouseDown}
    >
      {showRulers && (
        <Rulers
          composition={composition}
          zoom={previewAtJournalSize ? 1 : canvasZoom}
          containerWidth={containerRef.current ? containerRef.current.clientWidth : 0}
          containerHeight={containerRef.current ? containerRef.current.clientHeight : 0}
          previewAtJournalSize={previewAtJournalSize}
        />
      )}
      {selectionBoxStyle && <div style={selectionBoxStyle} />}
      {gridVisible && containerSize.width > 0 && (
        <GridOverlay
          grid={effectiveGrid}
          rowHeight={effectiveGrid.rowHeight}
          containerWidth={containerSize.width}
          containerHeight={containerSize.height}
          enabled={gridVisible}
          padding={16} // p-4 = 16px padding
        />
      )}
      <GridLayout
        className="figure-composition-layout"
        layout={layout}
        // Use visual canvas width - this allows canvas to grow beyond journal limits
        width={availableWidthPx}
        // Use dynamic cols based on visual canvas size - allows panels to be dragged beyond journal limits
        cols={dynamicCols}
        rowHeight={effectiveGrid.rowHeight}
        margin={effectiveGrid.margin}
        onLayoutChange={handleLayoutChange}
        onDragStart={handleDragStart}
        onDrag={handleDrag}
        onDragStop={handleDragStop}
        onResizeStart={handleResizeStart}
        onResize={handleResize}
        onResizeStop={handleResizeStop}
        compactType="vertical"
        isDraggable={true}
        isResizable={true}
      >
        {effectivePanels.map((panel) => (
          <div key={panel.id}>
            <Panel panel={panel} isResizing={resizingPanelId === panel.id} isReadOnly={isReadOnly} />
          </div>
        ))}
      </GridLayout>
    </div>
  );
}

